/**
 * @fileoverview Componente Organismo optimizado para la ventana principal de chat en el módulo Inbox.
 * @module InboxChatWindowMC
 * @category Component - Organism
 * @since 3.1.0
 * @version 3.1.0
 *
 * @typedef {Object} InboxChatWindowMCProps
 * @property {string} platform - Plataforma activa ("whatsapp", "messenger", "instagram")
 * @property {string} selectedChatMode - Modo de chat seleccionado ("chat", "comment", etc.)
 * @property {function} handleToggleChatMode - Handler para alternar el modo de chat
 *
 * @component InboxChatWindowMC
 * @category Component - Organism
 *
 * @description
 * Organism for the main Inbox chat window.
 * It shows the active conversation, the message list and the composer.
 * Business logic and UI stay separated.
 * The screen uses dedicated hooks, memoization and TailwindCSS.
 *
 * **Features:**
 * - Renders the active conversation and its messages.
 * - Empty state with icon when no conversation is selected.
 * - Header, message area and composer wired together.
 * - Drag-and-drop images via ChatMessagesDropzoneMC.
 * - Memoization and useCallback for performance.
 * - TailwindCSS with tw- classes.
 * - Works on WhatsApp, Messenger and Instagram.
 *
 * **Atomic Design:**
 * - Level: Organism.
 * - Composition: header, chat area and composer.
 * - Dependencies: HeaderChatWindowMC, ChatMessagesDropzoneMC, AreaChatWindowMC, InboxComposerMC, IconMC, EmptyScreenInboxMC.
 *
 * @param {InboxChatWindowMCProps} props - Component properties
 * @returns {JSX.Element} Rendered JSX element
 *
 * @see {@link HeaderChatWindowMC} Header component of chat
 * @see {@link AreaChatWindowMC} Message area component
 * @see {@link InboxComposerMC} Message composer component
 * @see {@link EmptyScreenInboxMC} Empty state for non-integrated platforms
 * @see {@link ../../../../.github/frontend/inbox/README.md} Main documentation of the Inbox module
 */
import React, { useMemo, useCallback, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import EmptyScreenInboxMC from '../../../../components/EmptyScreenInboxMC';
import {
  HeaderChatWindowMC,
  AreaChatWindowMC,
  InboxComposerMC
} from '../../../../modules/inbox/index';
import IconMC from '../../../../components/IconMC';
import useSelectedConversation from '../../../../hooks/shared/useSelectedConversation';
import useChatAvailability from '../../../../hooks/inbox/useChatAvailability';
import ChatMessagesDropzoneMC from '../../../../modules/inbox/components/ChatMessagesDropzoneMC';
import usePlatformIntegrationStatus from '../../../../modules/Integrations/usePlatformIntegrationStatus';

// Main component of the chat window of the Inbox
const InboxChatWindowMC = React.memo(({
  platform,
  onMobileClose,
  onMobileToggleRightSidebar,
  showMobileRightSidebar = false,
  isSidebar = false
}) => {
  const { t } = useTranslation('InboxMC');
  const conversation = useSelectedConversation();
  const retailerInfo = useSelector((state) => state.retailerUsersReducer.retailer_info);
  const {
    isPlatformConnected,
    isLoadingPlatformStatus
  } = usePlatformIntegrationStatus(platform, retailerInfo);
  const { isChatClosed } = useChatAvailability({ conversation });

  const canDropImagesOnChatColumn = Boolean(
    conversation
    && !isChatClosed
    && typeof ENV !== 'undefined'
    && ENV.CURRENT_AGENT_ROLE !== 'Supervisor'
  );

  // MS rollout → ≥1 integración activa; legacy → flags de retailer_info.
  // Mientras el status MS está unresolved, no mostrar empty de conectar.
  const isPlatformIntegrated = Boolean(
    !platform || isPlatformConnected || isLoadingPlatformStatus
  );

  // Memoization of empty state configuration
  const emptyStateConfig = useMemo(() => ({
    icon: 'message-circle-outline',
    iconSize: '5xl',
    iconClass: 'tw-text-blue-500',
    message: t('chatWindow.empty')
  }), [t]);

  // Optimized empty state rendering
  const renderEmptyState = useCallback(() => (
    <Suspense fallback={<div className="loading" />}>
      <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-bg-gray-50">
        {isPlatformIntegrated && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
            <IconMC
              name={emptyStateConfig.icon}
              size={emptyStateConfig.iconSize}
              className={emptyStateConfig.iconClass}
            />
            <div className="tw-text-sm tw-font-medium tw-text-gray-800">
              {emptyStateConfig.message}
            </div>
          </div>
        )}
        {platform && !isPlatformIntegrated && (
          <EmptyScreenInboxMC platform={platform} />
        )}
      </div>
    </Suspense>
  ), [platform, isPlatformIntegrated, emptyStateConfig]);

  // Early return: no conversation selected
  if (!conversation) {
    return renderEmptyState();
  }

  // Render main: active conversation
  return (
    <Suspense fallback={<div className="loading" />}>
      <div className="tw-wrapper tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-bg-white">
        <HeaderChatWindowMC
          onMobileClose={onMobileClose}
          onMobileToggleRightSidebar={onMobileToggleRightSidebar}
          showMobileRightSidebar={showMobileRightSidebar}
          isSidebar={isSidebar}
        />
        <ChatMessagesDropzoneMC canDrop={canDropImagesOnChatColumn}>
          <AreaChatWindowMC />
          <InboxComposerMC />
        </ChatMessagesDropzoneMC>
      </div>
    </Suspense>
  );
});

InboxChatWindowMC.displayName = 'InboxChatWindowMC';

export default InboxChatWindowMC;
